import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { QuickSendContext } from "contexts/QuickSend";
import { QuickSendContactsContext } from "contexts/QuickSendContacts";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { QuickSendErrorContext } from "contexts/QuickSendError";
import {
  getContactName,
  replaceBodyCopied,
} from "contexts/helpers/QuickSendHelpers";
import moment from "moment";
import { useContext, useState } from "react";
import { NewBentoContact } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { HandleChangePreviewFunction } from "schemas/outreach";

import { makeDeepCopy } from "utils/updateLocalState";

import SelectContact from "./SelectContact";
import styles from "./styles";

const renderVariant = (
  variant: string,
  brandName?: string,
  contactName?: string,
) => replaceBodyCopied(contactName, brandName, variant.replace(/<[^>]*>/g, ""));

interface Props {
  handleAddNewContact?: (
    newContact: NewBentoContact,
    handleClose: () => void,
  ) => void;
  changePreview: HandleChangePreviewFunction;
}

const SharedEmailParts = ({ handleAddNewContact, changePreview }: Props) => {
  const { errors, setErrors } = useContext(QuickSendErrorContext);
  const { bentoBrand, bentoContact } = useContext(QuickSendContext);
  const { outreachMessages, templateForQuickSend, setTemplateForQuickSend } =
    useContext(QuickSendContext);
  const { getLastContacted } = useContext(QuickSendContactsContext);
  const { quickSendBrandId, previewLoading } = useContext(
    QuickSendDrawerContext,
  );

  const [variantAnchor, setVariantAnchor] = useState<HTMLElement | null>(null);
  const subjectVariants = templateForQuickSend?.[0]?.subjectVariants ?? [];
  const hasVariants = subjectVariants.length > 1;
  const contactName = getContactName(bentoBrand, bentoContact);

  const handleSelectVariant = (variant: string) => {
    const rendered = renderVariant(
      variant,
      bentoBrand?.normalizedName,
      contactName,
    );
    changePreview("subject", rendered, 0);
    changePreview("subjectVariant", variant, 0);
    setTemplateForQuickSend((prev) => {
      const copy = makeDeepCopy(prev);
      if (copy && copy[0]) {
        copy[0]["subject"] = rendered;
      }
      return copy;
    });
    setVariantAnchor(null);
  };

  const handleEditSubject = (e: CustomEvent) => {
    const { value } = e.target;
    changePreview("subject", value, 0);
    setTemplateForQuickSend((prev) => {
      const copy = makeDeepCopy(prev);
      if (copy && copy[0]) {
        copy[0]["subject"] = value;
      }
      return copy;
    });
    setErrors((prevErrors) => {
      return {
        ...prevErrors,
        outreachSubject: false,
      };
    });
  };

  const lastContacted = bentoContact?.id
    ? getLastContacted(bentoContact?.id)
    : "";

  const isLoading =
    previewLoading === quickSendBrandId || quickSendBrandId !== bentoBrand?.id;

  return (
    <Box>
      {bentoBrand && (
        <SelectContact
          handleAddNewContact={handleAddNewContact}
          brand={bentoBrand}
        />
      )}

      {lastContacted && previewLoading !== quickSendBrandId && (
        <Box sx={[styles.subjectWrapper, styles.border]}>
          <Typography component="span" sx={styles.annotation}>
            Last Contacted On:
          </Typography>
          <Typography>
            {moment(lastContacted)?.format("MMM DD, YYYY")}
            <Box
              component="i"
              className="fa-solid fa-circle-check"
              sx={{ ml: 1, color: "green.light" }}
            />
          </Typography>
        </Box>
      )}

      <Box sx={[styles.subjectWrapper, { height: 40 }]}>
        <Typography component="span" sx={styles.annotation}>
          Subject:
        </Typography>
        {!isLoading && (
          <TextField
            size="small"
            value={outreachMessages[0]?.subject || ""}
            onChange={handleEditSubject}
            fullWidth
            variant="standard"
            placeholder={
              errors?.outreachSubject ? "Please fill in the subject" : "Subject"
            }
            error={errors.outreachSubject}
            InputProps={{
              disableUnderline: !errors.outreachSubject,
            }}
            inputProps={{
              sx: {
                "&::placeholder": {
                  color: errors?.outreachSubject ? "red" : `${grey[500]}`,
                  opacity: 1,
                },
              },
            }}
          />
        )}
        {!isLoading && hasVariants && (
          <>
            <Tooltip title="Pick a subject line">
              <IconButton
                size="small"
                onClick={(e) => setVariantAnchor(e.currentTarget)}
                sx={{ flexShrink: 0, color: "#008236", fontSize: 13, gap: 0.5 }}
              >
                <Box component="i" className="fa-solid fa-shuffle" />
                <Box component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
                  {subjectVariants.length}
                </Box>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={variantAnchor}
              open={Boolean(variantAnchor)}
              onClose={() => setVariantAnchor(null)}
            >
              {subjectVariants.map((variant, idx) => {
                const rendered = renderVariant(
                  variant,
                  bentoBrand?.normalizedName,
                  contactName,
                );
                const selected =
                  outreachMessages[0]?.subjectVariant === variant;
                return (
                  <MenuItem
                    key={idx}
                    selected={selected}
                    onClick={() => handleSelectVariant(variant)}
                    sx={{ fontSize: 14, maxWidth: 360, whiteSpace: "normal" }}
                  >
                    {rendered}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        )}
        {isLoading && <Skeleton animation="wave" width={100} />}
      </Box>
    </Box>
  );
};

export default SharedEmailParts;
